<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="[]"
    ref="gc-base-layout"
    class="mrp-layout"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Create New MRP'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="
          baseView.mode === 'create'
            ? 'Create New MRP'
            : 'MRP Run # ' + baseView.id
        "
        :secondaryLabel="undefined"
      />
    </template>

    <div class="css-v2 transaction-screen">
      <gc-header-form
        :headerOptions="getHeaderScreenJson"
        :data="loaded ? model : {}"
        :backButtonRoute="backButtonRoute"
        ref="gc-header-form-component"
        :getHeaderToolbar="undefined"
        :setLoading="showLoading"
        :formContentReady="onHeaderFormContentReady"
      ></gc-header-form>
    </div>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import { MRPService } from "../../../_services/api/demand-planning/mrp.service";
import { mapGetters } from "vuex";

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: {},
  data() {
    return {
      loaded: false,
      onFocusIn: false,
      headerForm: null,
      model: {
        IncludeStockOnhand: true,
        IncludeSalesForecast: true,
        IncludeSalesContract: true,
        IncludeSalesOrders: true,
        IncludePurchaseForecast: true,
        IncludePurchaseOrders: true,
        IncludeProcessRequirements: true,
        IncludeProductionForecast: true,
        IncludeWorkOrders: true,
        IncludeDraftWorkOrders: true,
        BillType: null,
        StartDate: null,
        EndDate: null,
        RetainUntil: null,
        BranchCodes: [],
        Narration: "",
        Clear: null,
        Generate: null,
      },
    };
  },
  async created() {
    if (this.baseView.mode !== "create") {
      await this.getMRPHeaderDetails();
    }

    /* Set the page key to force re-rendering when needed */
    this.$nextTick(() => {
      this.loaded = true;
    });
  },
  computed: {
    /**
     * Computed property to get the hotkey save state from Vuex store
     * @returns {boolean} The hotkey save state
     */
    ...mapGetters({ getHotKeySave: "hotKey/getHotKeySave" }),

    /**
     * Computed property to get the back button route
     * @returns {string} The back button route
     */
    backButtonRoute() {
      return "/mrp";
    },

    /**
     * Computed property to get the header screen JSON configuration
     * @returns {Object} The header screen JSON configuration
     */
    getHeaderScreenJson() {
      return {
        headerFormVisible: true,
        headerToolbar: {
          visible: false,
        },
        formItems: [
          {
            itemType: "group",
            caption: "",
            colCount: 3,
            items: [
              {
                itemType: "group",
                caption: "",
                colCount: 2,
                items: [
                  {
                    itemType: "group",
                    caption: "",
                    colCount: 1,
                    items: [
                      {
                        dataField: "IncludeStockOnhand",
                        label: { text: "Stock Onhand" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 1,
                        },
                      },
                      {
                        dataField: "IncludeSalesForecast",
                        label: { text: "Sales Forecast" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 2,
                        },
                      },
                      {
                        dataField: "IncludeSalesContract",
                        label: { text: "Sales Contract" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 3,
                        },
                      },
                      {
                        dataField: "IncludeSalesOrders",
                        label: { text: "Sales Orders" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 4,
                        },
                      },
                    ],
                  },
                  {
                    itemType: "group",
                    caption: "",
                    colCount: 1,
                    items: [
                      {
                        dataField: "IncludePurchaseForecast",
                        label: { text: "Purchase Forecast" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 5,
                        },
                      },
                      {
                        dataField: "IncludePurchaseOrders",
                        label: { text: "Purchase Orders" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 6,
                        },
                      },
                      {
                        dataField: "IncludeProcessRequirements",
                        label: { text: "Process Requirements" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 7,
                          onValueChanged: (e) => {
                            if (!e.event) return;
                            this.clearBillTypeValue(e);
                          },
                        },
                      },
                    ],
                  },
                ],
              },
              {
                itemType: "group",
                caption: "",
                colCount: 1,
                items: [
                  {
                    dataField: "IncludeProductionForecast",
                    label: { text: "Production Forecast" },
                    editorType: "dxSwitch",
                    editorOptions: {
                      tabIndex: 8,
                      onValueChanged: (e) => {
                        if (!e.event) return;
                        this.clearBillTypeValue(e);
                      },
                    },
                  },
                  {
                    itemType: "group",
                    caption: "",
                    colCount: 2,
                    items: [
                      {
                        dataField: "IncludeWorkOrders",
                        label: { text: "Work Orders" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 9,
                          onValueChanged: (e) => {
                            if (!e.event) return;
                            this.onWorkOrdersValueChange(e);
                          },
                        },
                      },
                      {
                        dataField: "IncludeDraftWorkOrders",
                        label: { text: "Include Draft Work Orders" },
                        editorType: "dxSwitch",
                        cssClass: "include-draft-work-orders",
                        editorOptions: {
                          tabIndex: 10,
                        },
                      },
                    ],
                  },
                  {
                    screenField: "BillType",
                    dataField: "BillType",
                    label: { text: "Bill Type" },
                    editorOptions: {
                      tabIndex: 11,
                      showClearButton: true,
                      readOnly: !(
                        this.model.IncludeProductionForecast ||
                        this.model.IncludeProcessRequirements
                      ),
                    },
                    dataSourceConfig: {
                      endpoint: "api/BillType",
                      sort: {
                        selector: "Description",
                        asc: true,
                      },
                      filter:
                        this.baseView.mode === "create"
                          ? [
                              ["Inactive", "=", "N"],
                              "and",
                              ["Code", "<>", "Maintenance"],
                            ]
                          : [["Inactive", "=", "N"]],
                    },
                    valueExpr: "Code",
                    displayExpr: "Description",
                    required:
                      this.model.IncludeProductionForecast ||
                      this.model.IncludeProcessRequirements,
                  },
                ],
              },
              {
                itemType: "group",
                caption: "",
                colCount: 1,
                items: [
                  {
                    itemType: "group",
                    caption: "",
                    colCount: 2,
                    cssClass: "date-group",
                    items: [
                      {
                        dataField: "StartDate",
                        label: { text: "Start Date" },
                        editorType: "dxDateBox",
                        editorOptions: {
                          displayFormat: "dd MMM yyyy",
                          tabIndex: 12,
                        },
                        cssClass: "start-date",
                      },
                      {
                        dataField: "EndDate",
                        label: { text: "End Date" },
                        editorType: "dxDateBox",
                        editorOptions: {
                          displayFormat: "dd MMM yyyy",
                          tabIndex: 13,
                        },
                        cssClass: "end-date",
                      },
                    ],
                  },
                  {
                    dataField: "RetainUntil",
                    label: { text: "Retain Until Date" },
                    editorType: "dxDateBox",
                    editorOptions: {
                      displayFormat: "dd MMM yyyy",
                      tabIndex: 14,
                      min: new Date(),
                      invalidDateMessage: null,
                    },
                    validationRules: [
                      {
                        type: "custom",
                        validationCallback: function (e) {
                          // allow empty
                          if (!e.value) return true;

                          const today = new Date();
                          today.setHours(0, 0, 0, 0); // Normalize time
                          const selectedDate = new Date(e.value);
                          selectedDate.setHours(0, 0, 0, 0);
                          return selectedDate >= today;
                        },
                        message: "Start Date cannot be in the past",
                      },
                    ],
                  },
                  {
                    screenField: "Branch",
                    dataField: "BranchCodes",
                    label: { text: "Branch" },
                    editorType: "dxTagBox",
                    multiSelect: true,
                    editorOptions: {
                      tabIndex: 15,
                    },
                  },
                ],
              },
            ],
          },

          {
            itemType: "group",
            caption: "",
            colCount: 1,
            items: [
              {
                dataField: "Narration",
                label: { text: "Narration" },
                labelLocation: "top",
                editorType: "dxTextArea",
                editorOptions: {
                  height: 90,
                  placeholder: "Enter narration here",
                  tabIndex: 16,
                  maxLength: 1000,
                },
              },
            ],
          },
          {
            itemType: "group",
            caption: "",
            colCount: 1,
            cssClass: "generate-button-group",
            visible: this.baseView.mode !== "review",
            items: [
              {
                dataField: "Clear",
                label: {
                  text: " ",
                  visible: false,
                },
                editorType: "dxButton",
                cssClass: "btn-clear",
                editorOptions: {
                  validationGroup: "gc-header-form",
                  onClick: async () => {
                    this.onClearClick();
                  },
                  text: "Clear",
                  type: "normal",
                  rtlEnabled: false,
                  tabIndex: 17,
                },
              },
              {
                dataField: "Generate",
                label: {
                  text: " ",
                  visible: false,
                },
                editorType: "dxButton",
                cssClass: "btn-generate",
                editorOptions: {
                  validationGroup: "gc-header-form",
                  onClick: async () => {
                    this.onGenerateClick();
                  },
                  text: "Generate",
                  type: "default",
                  rtlEnabled: false,
                  tabIndex: 18,
                },
              },
            ],
          },
        ],
      };
    },
  },
  methods: {
    initData() {
      this.model = {
        IncludeStockOnhand: true,
        IncludeSalesForecast: true,
        IncludeSalesContract: true,
        IncludeSalesOrders: true,
        IncludePurchaseForecast: true,
        IncludePurchaseOrders: true,
        IncludeProcessRequirements: true,
        IncludeProductionForecast: true,
        IncludeWorkOrders: true,
        IncludeDraftWorkOrders: true,
        BillType: null,
        StartDate: null,
        EndDate: null,
        RetainUntil: new Date().setMonth(new Date().getMonth() + 3),
        BranchCodes: [],
        Narration: "",
        Clear: null,
        Generate: null,
      };

      // Set default BillType value
      const billTypeEditor = this.headerForm.getEditor("BillType");
      if (billTypeEditor && !this.model.BillType) {
        const dataSource = billTypeEditor.getDataSource();
        dataSource.load().then((items) => {
          if (items && items.length > 0) {
            this.model.BillType = "Production";
          }
        });
      }
    },
    /**
     * Function to navigate back to the MRP list page
     * @returns {void}
     */
    backButtonFunction() {
      this.$router
        .push({
          path: this.backButtonRoute,
        })
        .then(() => {
          this.$store.dispatch("dataContext/setDataViewType", undefined);
        });
    },

    /**
     * Function to handle the form content ready event
     * @param {Object} e - The event object
     * @returns {void}
     */
    onHeaderFormContentReady(e) {
      if (e && this.baseView.mode != "review" && !this.onFocusIn) {
        this.onFocusIn = true;
        setTimeout(() => {
          this.headerForm = e.component;
          this.initData();
          this.focusOnHeaderForm();
          this.onWorkOrdersValueChange();
        }, 500);
      }
    },

    /**
     * Function to focus on the Stock On Hand field in the header form
     * @returns {void}
     */
    async focusOnHeaderForm() {
      setTimeout(() => {
        this.$nextTick(() => {
          if (
            this.headerForm &&
            this.headerForm.getEditor("IncludeStockOnhand")
          ) {
            let branchEditor = this.headerForm
              .getEditor("IncludeStockOnhand")
              .instance();
            if (branchEditor) {
              branchEditor.focus();
            }
          }
        });
      }, 100);
    },

    /**
     * Function to handle the value change event of the Work Orders field
     * Disables or enables the Include Draft Work Orders field based on the value of Work Orders
     * @returns {void}
     */
    onWorkOrdersValueChange() {
      const draftWorkOrdersEditor = this.headerForm.getEditor(
        "IncludeDraftWorkOrders"
      );
      if (draftWorkOrdersEditor) {
        if (!this.model.IncludeWorkOrders) {
          draftWorkOrdersEditor.option("disabled", true);
          draftWorkOrdersEditor.option("tabIndex", -1);
          draftWorkOrdersEditor.option("value", false);
        } else {
          draftWorkOrdersEditor.option("disabled", false);
          draftWorkOrdersEditor.option("tabIndex", 10);
        }
      }
    },

    clearBillTypeValue() {
      if (
        !this.model.IncludeProductionForecast &&
        !this.model.IncludeProcessRequirements
      ) {
        this.model.BillType = null;
      }
    },

    /**
     * Function to handle the Generate button click event
     * Validates the form and saves the MRP header if the form is valid
     * @returns {void}
     */
    async onGenerateClick() {
      const form =
        this.$refs["gc-header-form-component"].$refs["gc-header-form"];
      if (form) {
        const validations = await form.getFormValidations();

        // Check if at least one checkbox is selected
        const atLeastOneCheckboxSelected =
          this.model.IncludeStockOnhand ||
          this.model.IncludeSalesForecast ||
          this.model.IncludeSalesContract ||
          this.model.IncludeSalesOrders ||
          this.model.IncludePurchaseForecast ||
          this.model.IncludePurchaseOrders ||
          this.model.IncludeProcessRequirements ||
          this.model.IncludeProductionForecast ||
          this.model.IncludeWorkOrders;

        if (!validations.isValid) {
          var index = validations.brokenRules.findIndex(
            (x) => x.message == "Value is out of range"
          );
          if (index != -1) {
            validations.brokenRules[
              index
            ].message = `Please enter a Retain Until Date that is greater than or equal to ${new Date().toShortDateString()}.`;
          }
          this.displayValidationSummary(validations.brokenRules);
        } else if (!atLeastOneCheckboxSelected) {
          validations.brokenRules.push({
            message: "MRP run cannot be executed if all toggles are off",
          });
          this.displayValidationSummary(validations.brokenRules);
        } else {
          let confirmDelete = await this.$confirm(
            "We’re generating your MRP data (this may take several minutes).<br/>Do you want to proceed?",
            "Generate MRP"
          );
          if (confirmDelete) {
            this.saveMRPHeader();
          }
        }
      }
      // getFormValidations
    },

    /**
     * Function to save the MRP header
     * Displays a success notification and navigates back to the MRP list page upon successful save
     * @returns {void}
     */
    async saveMRPHeader() {
      this.showLoading(true);
      try {
        const response = await MRPService.save(this.getPayload());
        if (response) {
          this.$notify({
            message: "MRP Header created successfully.",
            type: "success",
          });
          this.setIgnoreRouteChangeConfirmation();
          this.$nextTick(() => {
            this.$router.push({ path: "/mrp" });
          });
        }
      } catch (error) {
        this.displayServerError(error);
      } finally {
        this.showLoading(false);
      }
    },

    /**
     * Function to construct the payload for saving the MRP header
     * @returns {Object} The payload object
     */
    getPayload() {
      const payload = {
        // MRPName: undefined,
        BillType: this.model.BillType,
        BranchCodes: this.model.BranchCodes.join(","),
        StartDate: this.model.StartDate
          ? this.model.StartDate.toSaveDate()
          : null,
        EndDate: this.model.EndDate ? this.model.EndDate.toSaveDate() : null,
        RetainUntil: this.model.RetainUntil
          ? new Date(this.model.RetainUntil).toSaveDate()
          : null,
        IncludeDraftWorkOrders: this.model.IncludeDraftWorkOrders,
        IncludeProcessRequirements: this.model.IncludeProcessRequirements,
        IncludeProductionForecast: this.model.IncludeProductionForecast,
        IncludePurchaseForecast: this.model.IncludePurchaseForecast,
        IncludePurchaseOrders: this.model.IncludePurchaseOrders,
        IncludeSalesContract: this.model.IncludeSalesContract,
        IncludeSalesForecast: this.model.IncludeSalesForecast,
        IncludeSalesOrders: this.model.IncludeSalesOrders,
        IncludeStockOnhand: this.model.IncludeStockOnhand,
        IncludeWorkOrders: this.model.IncludeWorkOrders,
        Narration: this.model.Narration,
      };

      return payload;
    },

    /**
     * Function to handle the Clear button click event
     * Resets the form values and focuses on the Stock On Hand field
     * @returns {void}
     */
    onClearClick() {
      const form = this.HeaderFormInstance();
      if (form) {
        // form.resetValues();
        this.initData();

        this.$nextTick(() => {
          this.focusOnHeaderForm();
          this.onWorkOrdersValueChange();
        });
      }
    },

    /**
     * Function to get the instance of the header form
     * @returns {Object} The header form instance
     */
    HeaderFormInstance() {
      if (this.loaded)
        return this.$refs["gc-header-form-component"].$refs["gc-header-form"]
          .$refs["gc-form-comp"].instance;
    },

    /**
     * Function to get the MRP header details for review mode
     * Populates the model with the retrieved data
     * @returns {void}
     */
    async getMRPHeaderDetails() {
      this.showLoading(true);
      try {
        const response = await MRPService.getMRPHeader(this.baseView.id);
        if (
          response &&
          response.data &&
          response.data.value &&
          response.data.value.length > 0
        ) {
          this.model = Object.assign(this.model, {
            ...response.data.value[0],
          });

          if (this.model.BranchCodes) {
            this.model.BranchCodes = this.model.BranchCodes.split(",");
          } else {
            this.model.BranchCodes = [];
          }

          if (this.model.StartDate) {
            this.model.StartDate = new Date(
              this.model.StartDate.replace("Z", "")
            );
          }

          if (this.model.EndDate) {
            this.model.EndDate = new Date(this.model.EndDate.replace("Z", ""));
          }

          if (this.model.RetainUntil) {
            this.model.RetainUntil = new Date(
              this.model.RetainUntil.replace("Z", "")
            );
          }
        }
      } catch (error) {
        this.displayServerError(error);
      } finally {
        this.showLoading(false);
      }
    },
  },
  watch: {
    /**
     * Watcher for the getHotKeySave computed property
     * Triggers the onGenerateClick method when getHotKeySave is true
     * @param {boolean} value - The new value of getHotKeySave
     * @returns {void}
     */
    getHotKeySave(value) {
      if (value) {
        this.$store.dispatch("hotKey/setHotKeySave", false);
        this.onGenerateClick();
      }
    },
  },
};
</script>

<style lang="scss">
.mrp-layout {
  .date-group {
    .start-date {
      padding-right: unset !important;

      @media (min-width: 1400px) {
        input {
          width: 75px !important;
        }
      }

      // @media (min-width: 1200px) {
      //   input {
      //     width: 50px !important;
      //   }
      // }
    }

    .end-date {
      // padding-left: 8px !important;
      > label {
        width: 75px;
      }
    }
  }

  .generate-button-group {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    .dx-widget.dx-collection {
      > .dx-box {
        flex-direction: row !important;
        gap: 20px;
      }
    }

    .btn-clear {
      padding-top: 5px !important;
      gap: 20px;
    }
  }

  .dx-tagbox:not(.dx-texteditor-empty):not(.dx-editor-filled):not(
      .dx-editor-outlined
    )::after {
    border-bottom: 1px solid rgba(0, 0, 0, 0.42) !important;
  }
}
</style>
